// @spec AUD-STR-044
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AcknowledgeAlertDialog,
  AlertHistoryList,
  AlertRulesList,
  CreateRuleDialog,
  DeleteRuleDialog,
  EditRuleDialog,
  ResolveAlertDialog,
  useAcknowledgeAlert,
  useAlertRules,
  useAlerts,
  useCreateAlertRule,
  useDeleteAlertRule,
  useResolveAlert,
  useToggleAlertRule,
  useUpdateAlertRule,
} from '@/features/settings/audit-alerts';
import type { Alert, AlertRule } from '@/features/settings/audit-alerts';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/audit-alerts')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.auditAlertRules'),
  }),
  component: AuditAlertsPage,
});

/**
 * Audit alert rules management page.
 *
 * Shows list of alert rules and triggered alerts. Allows admins to create,
 * edit, toggle, and delete rules, as well as acknowledge and resolve alerts.
 */
function AuditAlertsPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin, isLoading: isRoleLoading } = useOrgRole();

  // State for dialogs
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [deletingRule, setDeletingRule] = useState<AlertRule | null>(null);
  const [acknowledgingAlert, setAcknowledgingAlert] = useState<Alert | null>(null);
  const [resolvingAlert, setResolvingAlert] = useState<Alert | null>(null);

  // Get internal org ID from Clerk organization metadata
  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  // Fetch data - hooks must be called before any early returns
  const { data: rulesData, isLoading: isLoadingRules } = useAlertRules();
  const { data: alertsData, isLoading: isLoadingAlerts } = useAlerts();

  // Mutations - must be called before any early returns
  const createRule = useCreateAlertRule({
    onSuccess: () => {
      setIsCreateDialogOpen(false);
      toast.success(t('settings:toast.alertRuleCreated'), {
        description: t('auditAlertsPage.ruleCreatedDescription'),
      });
    },
    onError: (error) => {
      toast.error(t('settings:toast.failedToCreateRule'), {
        description: error.message,
      });
    },
  });

  const updateRule = useUpdateAlertRule({
    onSuccess: () => {
      setEditingRule(null);
      toast.success(t('settings:toast.alertRuleUpdated'), {
        description: t('auditAlertsPage.ruleUpdatedDescription'),
      });
    },
    onError: (error) => {
      toast.error(t('settings:toast.failedToUpdateRule'), {
        description: error.message,
      });
    },
  });

  const toggleRule = useToggleAlertRule({
    onSuccess: (response) => {
      toast.success(
        response.isActive
          ? i18n.t('settings:toast.alertRuleActivated')
          : i18n.t('settings:toast.alertRulePaused'),
        {
          description: response.isActive
            ? t('auditAlertsPage.ruleActivatedDescription')
            : t('auditAlertsPage.rulePausedDescription'),
        }
      );
    },
    onError: (error) => {
      toast.error(t('settings:toast.failedToToggleRule'), {
        description: error.message,
      });
    },
  });

  const deleteRule = useDeleteAlertRule({
    onSuccess: () => {
      setDeletingRule(null);
      toast.success(t('settings:toast.alertRuleDeleted'), {
        description: t('auditAlertsPage.ruleDeletedDescription'),
      });
    },
    onError: (error) => {
      toast.error(t('settings:toast.failedToDeleteRule'), {
        description: error.message,
      });
    },
  });

  const acknowledgeAlert = useAcknowledgeAlert({
    onSuccess: () => {
      setAcknowledgingAlert(null);
      toast.success(t('settings:toast.alertAcknowledged'), {
        description: t('auditAlertsPage.alertAcknowledgedDescription'),
      });
    },
    onError: (error) => {
      toast.error(t('settings:toast.failedToAcknowledgeAlert'), {
        description: error.message,
      });
    },
  });

  const resolveAlert = useResolveAlert({
    onSuccess: () => {
      setResolvingAlert(null);
      toast.success(t('settings:toast.alertResolved'), {
        description: t('auditAlertsPage.alertResolvedDescription'),
      });
    },
    onError: (error) => {
      toast.error(t('settings:toast.failedToResolveAlert'), {
        description: error.message,
      });
    },
  });

  // Redirect non-admins to dashboard
  useEffect(() => {
    if (!isRoleLoading && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isRoleLoading, isAdmin, navigate]);

  // Don't render content while checking permissions
  if (!isRoleLoading && !isAdmin) {
    return null;
  }

  // Show skeleton while loading org info
  if (isRoleLoading || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <header>
            <Skeleton className="h-9 w-64" />
            <Skeleton className="mt-2 h-5 w-96" />
          </header>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('auditAlertsPage.title')}</h1>
          <p className="mt-1 text-muted-foreground">{t('auditAlertsPage.description')}</p>
        </header>

        <Tabs defaultValue="rules">
          <TabsList>
            <TabsTrigger value="rules">{t('auditAlertsPage.rulesTab')}</TabsTrigger>
            <TabsTrigger value="history">{t('auditAlertsPage.historyTab')}</TabsTrigger>
          </TabsList>

          <TabsContent value="rules" className="mt-6">
            <AlertRulesList
              rules={rulesData?.rules}
              isLoading={isLoadingRules}
              onCreateClick={() => {
                setIsCreateDialogOpen(true);
              }}
              onEditClick={(rule) => {
                setEditingRule(rule);
              }}
              onToggleClick={(rule) => {
                toggleRule.mutate({
                  ruleId: rule.id,
                  request: { is_active: !rule.isActive },
                });
              }}
              onDeleteClick={(rule) => {
                setDeletingRule(rule);
              }}
              isAdmin={isAdmin}
            />
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            <AlertHistoryList
              alerts={alertsData?.alerts}
              isLoading={isLoadingAlerts}
              onAcknowledgeClick={(alert) => {
                setAcknowledgingAlert(alert);
              }}
              onResolveClick={(alert) => {
                setResolvingAlert(alert);
              }}
              isAdmin={isAdmin}
            />
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <CreateRuleDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onSubmit={(data) => {
            createRule.mutate(data);
          }}
          isSubmitting={createRule.isPending}
        />

        {editingRule && (
          <EditRuleDialog
            rule={editingRule}
            open={!!editingRule}
            onOpenChange={(open) => {
              if (!open) setEditingRule(null);
            }}
            onSubmit={(data) => {
              updateRule.mutate({
                ruleId: editingRule.id,
                request: data,
              });
            }}
            isSubmitting={updateRule.isPending}
          />
        )}

        {deletingRule && (
          <DeleteRuleDialog
            rule={deletingRule}
            open={!!deletingRule}
            onOpenChange={(open) => {
              if (!open) setDeletingRule(null);
            }}
            onConfirm={() => {
              deleteRule.mutate(deletingRule.id);
            }}
            isDeleting={deleteRule.isPending}
          />
        )}

        {acknowledgingAlert && (
          <AcknowledgeAlertDialog
            alert={acknowledgingAlert}
            open={!!acknowledgingAlert}
            onOpenChange={(open) => {
              if (!open) setAcknowledgingAlert(null);
            }}
            onConfirm={(note) => {
              acknowledgeAlert.mutate({
                alertId: acknowledgingAlert.id,
                request: { note: note ?? null },
              });
            }}
            isSubmitting={acknowledgeAlert.isPending}
          />
        )}

        {resolvingAlert && (
          <ResolveAlertDialog
            alert={resolvingAlert}
            open={!!resolvingAlert}
            onOpenChange={(open) => {
              if (!open) setResolvingAlert(null);
            }}
            onConfirm={(resolution) => {
              resolveAlert.mutate({
                alertId: resolvingAlert.id,
                request: { resolution: resolution ?? null },
              });
            }}
            isSubmitting={resolveAlert.isPending}
          />
        )}
      </div>
    </div>
  );
}
